import cn from 'classnames';
import { type Dispatch, type FC, useCallback, useMemo, useRef, useState } from 'react';

import { _isNotEmpty, _notNil } from '@/littledash';

import type { Animal } from '@/model/Animal.model';
import type { ISODateTime } from '@/model/Common.model';
import type { TeamGlossaryV1 } from '@/model/Glosary.model';
import InVivoError from '@/model/InVivoError';

import { errorToast, successToast } from '@/helpers';
import Http from '@/support/http';
import { api as apiRoutes } from '@/support/route';
import { DateUtils } from '@/utils/Date.utils';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import StepForm from '@/components/UI/StepForm';
import type { Step, StepFormAction, StepFormState } from '@/components/UI/StepForm/StepForm.model';

import { initialObservationState, validateObservation } from './AddObservation.utils';
import ObservationForm from './ObservationForm';

interface ObservationDraft {
  team_glossary_api_id: string;
  title?: string | null;
  description?: string | null;
  type?: TeamGlossaryV1['type'] | null;
  text: string;
  options?: TeamGlossaryV1['options'];
  observed_at?: ISODateTime;
  value?: string | number | null;
}

type ObservationValidationErrors = false | Record<string, Array<string>>;

interface ObservationCreatePayload {
  team_glossary_api_id: string;
  value?: string | number | null;
  text: string;
  observed_at?: ISODateTime;
}

type AddObservationsStepProps = Record<string, unknown>;

type AddObservationsStepState = StepFormState<AddObservationsStepState> & {
  currentStep: number;
  steps: Array<Step<AddObservationsStepState, AddObservationsStepProps>>;
};

interface AddObservationsProps {
  animalId: Animal['id'];
  selectedObservations: Array<TeamGlossaryV1>;
  observed_at?: ISODateTime;
  closeModal: () => void;
  handleCallback: () => void;
}

const buildInitialObservations = (
  selectedObservations: Array<TeamGlossaryV1>,
  observedAt?: ISODateTime
): Array<ObservationDraft> =>
  selectedObservations.map((observation) =>
    initialObservationState({
      team_glossary_api_id: observation.api_id,
      title: observation.title,
      description: observation.description,
      type: observation.type,
      text: '',
      options: observation.options,
      observed_at: observedAt,
    })
  );

/**
 * Multi-step modal for recording one or more observations against an animal.
 */
const AddObservations: FC<AddObservationsProps> = ({
  animalId,
  selectedObservations,
  observed_at: observedAt,
  closeModal,
  handleCallback,
}) => {
  const [observations, setObservations] = useState<Array<ObservationDraft>>(() =>
    buildInitialObservations(selectedObservations, observedAt)
  );
  const [loading, setLoading] = useState(false);
  const [submissionError, setSubmissionError] = useState<unknown | null>(null);
  const submittingRef = useRef(false);

  const errors = useMemo(
    () => observations.map((observation) => validateObservation(observation) as ObservationValidationErrors),
    [observations]
  );

  const handleInput = useCallback((index: number, key: 'text' | 'value', val: string | number | null) => {
    setObservations((previousObservations) =>
      previousObservations.map((observation, observationIndex) =>
        observationIndex === index ? { ...observation, [key]: val } : observation
      )
    );
  }, []);

  const handlePayload = useCallback(
    (observation: ObservationDraft): ObservationCreatePayload => {
      const { team_glossary_api_id, value, text } = observation;
      const payload: ObservationCreatePayload = {
        team_glossary_api_id,
        value,
        text,
      };

      if (_notNil(observedAt) && DateUtils.isValidDateTime(observedAt)) {
        return {
          ...payload,
          observed_at: observedAt,
        };
      }

      return payload;
    },
    [observedAt]
  );

  const handleSubmit = useCallback(
    async (_state: AddObservationsStepState, dispatch: Dispatch<StepFormAction<AddObservationsStepState>>) => {
      const isValid = errors.every((observationErrors) => observationErrors === false);
      // Guard key-repeat: CSS ui__disabled only blocks pointer events, not Enter.
      if (!isValid || loading || submittingRef.current) {
        return;
      }

      submittingRef.current = true;
      setLoading(true);
      dispatch({ type: 'finalStepReady', data: false });
      setSubmissionError(null);

      try {
        const promises = observations.map((observation) =>
          Http.post(apiRoutes('observations.create', { id: animalId }), handlePayload(observation))
        );
        await Promise.all(promises);
        closeModal();
        handleCallback();
        successToast(`Observation${observations.length > 1 ? 's' : ''} saved`);
      } catch (error) {
        setSubmissionError(error);
        const ivError = new InVivoError('Could not save observation(s)', {
          slug: 'add-observations-submit',
          cause: error,
        });
        ExceptionHandler.captureException(ivError);
        errorToast('Could not save observation(s)');
      } finally {
        submittingRef.current = false;
        setLoading(false);
        dispatch({ type: 'finalStepReady', data: true });
      }
    },
    [errors, observations, animalId, handlePayload, closeModal, handleCallback, loading]
  );

  const steps = useMemo(
    () =>
      observations.map(
        (observation, index): Step<AddObservationsStepState, AddObservationsStepProps> => ({
          name: observation.title ?? `Observation ${index + 1}`,
          isValid: () => errors[index] === false,
          renderStep: (_state, dispatch) => (
            <div className="ui-card pa3">
              <ObservationForm
                key={observation.team_glossary_api_id}
                index={index}
                errors={errors[index]}
                handleInput={handleInput}
                observation={observation}
                dispatch={dispatch}
                width={undefined}
              />
            </div>
          ),
        })
      ),
    [observations, errors, handleInput]
  );

  const initialState = useMemo<AddObservationsStepState>(
    () => ({
      currentStep: 0,
      steps,
    }),
    [steps]
  );

  return (
    <div
      className={cn('w-75-l center ma3 bg-light-gray br2 shadow-4 mv4 relative pa4', {
        ui__disabled: loading,
      })}
      data-test-component="AddObservations"
      data-test-element="container"
    >
      {_notNil(submissionError) && (
        <Banner critical dismiss={false} className="mw6 mb3" testId="submission-error-banner">
          <h3 className="f5 normal lh-title pb2">There was an error with your submission</h3>
          <p className="f6 pb3 lh-copy">
            An error has occurred when submitting the form, please try again later. If this keeps occurring please
            contact support.
          </p>
          <Button outline critical url={'mailto:support@benchling.com'}>
            Contact support
          </Button>
        </Banner>
      )}
      {_isNotEmpty(observations) && (
        <StepForm
          title=""
          titlePadding="pb0"
          steps={steps}
          initialState={initialState}
          props={{}}
          horizontal={false}
          finalStepAction={handleSubmit}
          cancelFunction={closeModal}
          nextOnEnter
        />
      )}
    </div>
  );
};

export default AddObservations;
